import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Card, Table, Badge, Spinner, Button, Row, Col } from "react-bootstrap";
import { Icon } from "../../../components/common/Icon";
import {
  FaComments,
  FaEye,
  FaTrash,
  FaRobot,
  FaEnvelope,
  FaFacebookMessenger,
  FaEyeSlash,
  FaEyeDropper,
} from "react-icons/fa";
import { useTranslation } from "react-i18next";
import Pagination from "../../../components/common/Pagination";
import AIChatbotConversationModal from "./AIChatbotConversationModal";
import ConfirmationModal from "../../../components/common/ConfirmationModal";
import AIChatbotSessionFilter from "./AIChatbotSessionFilter";
import { fetchConversationsList } from "../../../features/ai_chatbots/aiChatbotsSlice";
import { AppDispatch, RootState } from "../../../store";

// Updated interface to match the actual API response
interface Conversation {
  id: number;
  config_id: number; // This is the chatbot ID
  session_id: string;
  created_at: string;
  updated_at: string;
  chatbot_name: string;
}

// API response interfaces
interface PaginationInfo {
  current_page: number;
  page_size: number;
  total_count: number;
  total_pages: number;
  has_next: boolean;
  has_previous: boolean;
}

interface ConversationResponse {
  data: Conversation[];
  pagination: PaginationInfo;
}

// Filter values interface
interface FilterValues {
  chatbot_id: string;
  session_id: string;
  start_date: string;
  end_date: string;
}

interface AIChatbotSessionsProps {
  defaultChatbotId?: number;
  hideChatbotSelect?: boolean;
}

const AIChatbotSessions: React.FC<AIChatbotSessionsProps> = ({ defaultChatbotId, hideChatbotSelect = false }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { conversations, isLoading, error, totalCount, page, limit } = useSelector(
    (state: RootState) => state.aiChatbots
  );
  const { t } = useTranslation();

  // Local state for pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [currentTotalCount, setCurrentTotalCount] = useState(0);

  // Filter state
  const [filters, setFilters] = useState<FilterValues>({
    chatbot_id: defaultChatbotId ? String(defaultChatbotId) : "",
    session_id: "",
    start_date: "",
    end_date: "",
  });

  // New state for conversation modal
  const [showConversationModal, setShowConversationModal] = useState(false);
  const [selectedConversation, setSelectedConversation] =
    useState<Conversation | null>(null);

  // State for delete confirmation
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Updated to accept parameters for page and filters
  const fetchConversations = async (pageNum = currentPage, currentFilters = filters) => {
    try {
      // Calculate skip based on page and limit
      const skip = (pageNum - 1) * limit;
      
      // Build query parameters
      const params: any = {
        skip,
        limit,
        page: pageNum
      };
      
      // Add filters to query params - using passed filters, not state
      if (currentFilters.chatbot_id) params.chatbot_id = currentFilters.chatbot_id;
      if (currentFilters.session_id) params.session_id = currentFilters.session_id;
      if (currentFilters.start_date) params.start_date = currentFilters.start_date;
      if (currentFilters.end_date) params.end_date = currentFilters.end_date;

      console.log("Fetching with params:", params); // Debug

      const result = await dispatch(fetchConversationsList(params));
      
      // Update local pagination state based on response
      if (result.payload && result.payload.pagination) {
        setCurrentPage(result.payload.pagination.current_page);
        setCurrentTotalCount(result.payload.pagination.total_count);
      } else if (result.payload && result.payload.data) {
        // Fallback for non-paginated response
        setCurrentPage(1);
        setCurrentTotalCount(result.payload.data.length);
      }
    } catch (err) {
      console.error("Error fetching conversations:", err);
    }
  };

  // Use effect with dependencies for page and filters
  useEffect(() => {
    fetchConversations();
  }, []); // Only run once on component mount

  // When defaultChatbotId changes while mounted, update filter and refetch
  useEffect(() => {
    if (defaultChatbotId) {
      const newFilters = { ...filters, chatbot_id: String(defaultChatbotId) };
      setFilters(newFilters);
      setCurrentPage(1);
      fetchConversations(1, newFilters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultChatbotId]);

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    fetchConversations(newPage, filters);
  };

  const handleViewConversation = (conversation: Conversation) => {
    setSelectedConversation(conversation);
    setShowConversationModal(true);
  };

  const handleDeleteClick = (conversation: Conversation) => {
    setSelectedConversation(conversation);
    setShowDeleteModal(true);
  };

  const handleDeleteConversation = async () => {
    if (!selectedConversation) return;

    try {
      // This API endpoint is hypothetical - implement according to your backend
      // For now, just close the modal and refresh the list
      setShowDeleteModal(false);
      
      // Optionally refetch data to ensure accurate counts
      fetchConversations();
    } catch (err) {
      console.error("Error deleting conversation:", err);
      // Could add error handling here
    }
  };

  // Updated filter change handler - pass filters directly to fetch function
  const handleFilterChange = (newFilters: FilterValues) => {
    // Set state for later reference
    setFilters(newFilters);
    // Reset to first page and fetch with new filters directly
    setCurrentPage(1);
    fetchConversations(1, newFilters);
  };

  // Updated filter reset handler
  const handleFilterReset = () => {
    const resetFilters = {
      chatbot_id: "",
      session_id: "",
      start_date: "",
      end_date: "",
    };
    // Set state for later reference
    setFilters(resetFilters);
    // Reset pagination and fetch with reset filters directly
    setCurrentPage(1);
    fetchConversations(1, resetFilters);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + " " + date.toLocaleTimeString();
  };

  const getSessionTypeIndicator = (sessionId: string) => {
    if (sessionId.startsWith("test_")) {
      return <Badge bg="warning">Test</Badge>;
    } else if (sessionId.startsWith("session_")) {
      return <Badge bg="success">Live</Badge>;
    } else {
      return <Badge bg="info">Other</Badge>;
    }
  };

  const formatSessionId = (sessionId: string) => {
    // If it's a long UUID, truncate it
    if (sessionId.length > 20) {
      return (
        sessionId.substring(0, 10) +
        "..." +
        sessionId.substring(sessionId.length - 4)
      );
    }
    return sessionId;
  };

  const renderContent = () => {
    if (isLoading && conversations.length === 0) {
      return (
        <div className="loading-container">
          <div className="loading-content">
            <Spinner animation="border" className="loading-spinner" />
            <p className="loading-text">{t("loading_sessions")}...</p>
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <Card className="error-card">
          <Card.Body>
            <div className="error-message">
              <Icon icon={FaComments} />
              <p>{error}</p>
            </div>
            <Button variant="primary" onClick={() => fetchConversations()}>
              {t("try_again")}
            </Button>
          </Card.Body>
        </Card>
      );
    }

    if (conversations.length === 0) {
      return (
        <Card className="empty-state-card">
          <Card.Body>
            <div className="empty-icon">
              <Icon icon={FaComments} />
            </div>
            <h3 className="empty-title">{t("no_chatbot_sessions_found")}</h3>
            <p className="empty-description">
              {t("there_are_no_active_chatbot_sessions_at_the_moment")}
            </p>
            {(filters.chatbot_id || filters.session_id || filters.start_date || filters.end_date) && (
              <div className="mt-3">
                <p>{t("try_adjusting_your_filters_or_clear_them")}</p>
                <Button variant="outline-primary" onClick={handleFilterReset}>
                  {t("clear_filters")}
                </Button>
              </div>
            )}
          </Card.Body>
        </Card>
      );
    }

    return (
      <Card>
        <Card.Header className="card-header-custom">
          <div className="section-header">
            <div className="section-icon session-icon">
              <Icon icon={FaComments} />
            </div>
            <h5 className="section-title">{t("chatbot_sessions")}</h5>
          </div>
        </Card.Header>
        <Card.Body className="p-0">
          <div className="table-responsive">
            <Table hover className="sessions-table">
              <thead>
                <tr>
                  <th>{t("id")}</th>
                  <th>{t("chatbot_name")}</th>
                  <th>{t("session_id")}</th>
                  <th>{t("type")}</th>
                  <th>{t("created_at")}</th>
                  <th>{t("last_updated")}</th>
                  <th>{t("actions")}</th>
                </tr>
              </thead>
              <tbody>
                {conversations.map((conversation) => (
                  <tr key={conversation.id}>
                    <td>{conversation.id}</td>
                    <td>
                      <div className="d-flex align-items-center">
                        <Icon icon={FaRobot} className="me-2 text-primary" />
                        {conversation.chatbot_name}
                      </div>
                    </td>
                    <td>
                      <span className="session-id">
                        {formatSessionId(conversation.session_id)}
                      </span>
                    </td>
                    <td>{getSessionTypeIndicator(conversation.session_id)}</td>
                    <td>{formatDate(conversation.created_at)}</td>
                    <td>{formatDate(conversation.updated_at)}</td>
                    <td>
                      <div className="action-buttons">
                        <Button
                          variant="outline-info"
                          size="sm"
                          className="action-btn"
                          title={t("view_messages")}
                          onClick={() => handleViewConversation(conversation)}
                        >
                          <Icon icon={FaEye} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </Card.Body>
        <Card.Footer>
          <div className="pagination-container">
            <div className="pagination-info">
              {isLoading ? (
                <Spinner animation="border" size="sm" className="me-2" />
              ) : (
                <>
                  {t("showing")} {conversations.length} {t("of")}{" "}
                  {currentTotalCount} {t("sessions")}
                </>
              )}
            </div>
            <Pagination
              currentPage={currentPage}
              totalCount={Math.ceil(currentTotalCount / limit)}
              pageSize={limit}
              onPageChange={handlePageChange}
            />
          </div>
        </Card.Footer>
      </Card>
    );
  };

  return (
    <div className="chatbot-sessions">
      {/* Filter Component */}
      <AIChatbotSessionFilter 
        onFilterChange={handleFilterChange}
        onReset={handleFilterReset}
        loading={isLoading}
        defaultChatbotId={defaultChatbotId}
        hideChatbotSelect={hideChatbotSelect}
      />

      {/* Main Content */}
      {renderContent()}

      {/* Conversation Modal */}
      {selectedConversation && (
        <AIChatbotConversationModal
          show={showConversationModal}
          onHide={() => setShowConversationModal(false)}
          conversationId={selectedConversation.id}
          sessionId={selectedConversation.session_id}
          chatbotId={selectedConversation.config_id}
          chatbotName={selectedConversation.chatbot_name}
        />
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        show={showDeleteModal}
        onCancel={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConversation}
        title={t("delete_session")}
        message={t("are_you_sure_delete_session")}
        confirmText={t("delete")}
      />

      <style>{`
        .chatbot-sessions {
          margin-top: 1rem;
        }
        
        .sessions-table th, 
        .sessions-table td {
          vertical-align: middle;
          padding: 12px 16px;
        }
        
        .session-icon {
          background: linear-gradient(135deg, #0ea5e9, #2563eb);
          color: white;
        }
        
        .session-id {
          font-family: monospace;
          background: var(--bg-tertiary);
          padding: 2px 8px;
          border-radius: 4px;
          font-size: 12px;
        }
        
        .action-buttons {
          display: flex;
          gap: 8px;
        }
        
        .action-btn {
          padding: 4px 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 4px;
        }
        
        .empty-state-card {
          text-align: center;
          padding: 3rem 1rem;
        }
        
        .empty-icon {
          width: 80px;
          height: 80px;
          margin: 0 auto 1.5rem;
          background: linear-gradient(135deg, #0ea5e9, #2563eb);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 32px;
        }
        
        .error-card {
          text-align: center;
          padding: 2rem;
        }
        
        .error-message {
          margin-bottom: 1.5rem;
          color: var(--danger-color);
        }

        .loading-container {
          display: flex;
          justify-content: center;
          align-items: center;
          min-height: 200px;
        }

        .loading-content {
          text-align: center;
        }

        .loading-spinner {
          margin-bottom: 1rem;
          color: var(--primary-color);
        }

        /* Mobile Responsive */
        @media (max-width: 768px) {
          .sessions-table {
            min-width: 700px;
          }
        }
      `}</style>
    </div>
  );
};

export default AIChatbotSessions;